import React, { useEffect, useState } from 'react'
import { ScaleLoader } from 'react-spinners'
import { useAPI } from '../../../Contexts/APIContext'
import { useQueryClient } from '@tanstack/react-query'
import { sleep } from '../../../Helpers/Timer+Sleep'
import { ModalWindow } from '../../GeneralComponents/ModalWindow/ModalWindow'
import { ButtonOutline } from '../../GeneralComponents/ButtonOutline/ButtonOutline'
import PropTypes from 'prop-types'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import albumSocialIcon from '../../icons/social_icons/album_social_icon_no_bg.png'
import discordSocialIcon from '../../icons/social_icons/discord_social_icon_no_bg.png'
import instaSocialIcon from '../../icons/social_icons/insta_social_icon_no_bg.png'
import merchSocialIcon from '../../icons/social_icons/merch_social_icon_no_bg.png'
import ticketsSocialIcon from '../../icons/social_icons/tickets_social_icon_no_bg.png'
import ticketmasterSocialIcon from '../../icons/social_icons/ticketmaster_no_bg.png'
import tiktokSocialIcon from '../../icons/social_icons/tiktok_social_icon_no_bg.png'
import xSocialIcon from '../../icons/social_icons/x-logo.png'
import youtubeSocialIcon from '../../icons/social_icons/youtube_social_icon_no_bg.png'
import ButtonIcon from '../../GeneralComponents/ButtonIcon'
import { ReactComponent as CameraIcon } from '../../icons/ph_camera.svg'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import classes from './index.module.css'

const fieldsConfig = [
  { key: 'artistName', label: 'Artist Name' },
  { key: 'youtube', label: 'Youtube', icon: youtubeSocialIcon },
  { key: 'instagram', label: 'Instagram', icon: instaSocialIcon },
  { key: 'twitter', label: 'X', icon: xSocialIcon },
  { key: 'tiktok', label: 'TikTok', icon: tiktokSocialIcon },
  { key: 'discord', label: 'Discord', icon: discordSocialIcon },
  { key: 'merch', label: 'Merch', icon: merchSocialIcon },
  { key: 'tour', label: 'Tour', icon: ticketsSocialIcon },
  { key: 'ticketmaster', label: 'Ticketmaster', icon: ticketmasterSocialIcon },
  { key: 'impact', label: 'Impact', icon: albumSocialIcon },
]

export default function ArtistEditData({
  isOpenModal,
  setIsOpenModal,
  artist,
}) {
  const { uploadArtistImage, uploadArtistData, getArtistImageUrl } = useAPI()
  const queryClient = useQueryClient()
  const { isInternal } = useAdminAccess()

  const [formData, setFormData] = useState({
    artistName: '',
    youtube: '',
    instagram: '',
    twitter: '',
    tiktok: '',
    discord: '',
    merch: '',
    tour: '',
    ticketmaster: '',
    impact: '',
  })
  const [errors, setErrors] = useState({})
  const [selectedProfileImageFile, setSelectedProfileImageFile] = useState(null)
  const [selectedProfileImageURL, setSelectecProfileImageURL] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const ticketmasterLink = artist?.links?.find(i => i.f_id === 'ticketmaster');
    
    // Extract just the ID from Ticketmaster URL if it's a full URL
    let ticketmasterValue = ticketmasterLink?.url ?? '';
    if (ticketmasterValue && ticketmasterValue.includes('ticketmaster.com/artist/')) {
      const match = ticketmasterValue.match(/\/artist\/(\d+)/);
      if (match) {
        ticketmasterValue = match[1]; // Show just the ID in the form
      }
    }
    
    setFormData({
      artistName: artist.name ?? '',
      youtube: artist?.social_media?.find(i => i.f_id === 'youtube')?.url ?? '',
      instagram:
        artist?.social_media?.find(i => i.f_id === 'instagram')?.url ?? '',
      twitter: artist?.social_media?.find(i => i.f_id === 'twitter')?.url ?? '',
      tiktok: artist?.social_media?.find(i => i.f_id === 'tiktok')?.url ?? '',
      discord: artist?.social_media?.find(i => i.f_id === 'discord')?.url ?? '',
      merch: artist?.links?.find(i => i.f_id === 'merchandise')?.url ?? '',
      tour: artist?.links?.find(i => i.f_id === 'tour')?.url ?? '',
      ticketmaster: ticketmasterValue,
      impact: artist?.links?.find(i => i.f_id === 'impact')?.url ?? '',
    })
  }, [artist])

  const validate = () => {
    const newErrors = {}
    if (!formData.artistName.trim()) {
      newErrors.artistName = 'Name is required'
    }

    const validateUrl = url => {
      try {
        if (!url) return true
        new URL(url)
        return true
      } catch {
        return false
      }
    }

    Object.entries(formData).forEach(([key, value]) => {
      if (['artistName', 'ticketmaster'].includes(key)) return
      if (value && !validateUrl(value)) {
        newErrors[key] = 'Invalid URL'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleOnSave = async () => {
    if (!validate()) return

    setIsSaving(true)

    try {
      if (selectedProfileImageFile) {
        const uploadLink = await getArtistImageUrl(
          artist.artist_name,
          selectedProfileImageFile.type,
          selectedProfileImageFile.size
        )

        await uploadArtistImage(
          uploadLink.upload_url[0],
          selectedProfileImageFile,
          selectedProfileImageFile.type
        )
      }

      // Convert Ticketmaster ID to full URL if it's just an ID
      let ticketmasterUrl = formData.ticketmaster;
      if (ticketmasterUrl && ticketmasterUrl.trim() !== '') {
        // Check if it's just an ID (alphanumeric with underscores and hyphens, without URL parts)
        if (/^[A-Za-z0-9_-]+$/.test(ticketmasterUrl.trim())) {
          // If it's just an ID, convert to full URL
          ticketmasterUrl = `https://www.ticketmaster.com/artist/${ticketmasterUrl.trim()}`;
        }
        // Otherwise keep the URL as is
      } else {
        // If empty or whitespace only, explicitly set to empty string
        ticketmasterUrl = '';
      }

      await uploadArtistData(
        artist.artist_name,
        formData.artistName,
        formData.instagram,
        formData.twitter,
        formData.youtube,
        formData.tiktok,
        formData.discord,
        formData.merch,
        formData.tour,
        formData.impact,
        ticketmasterUrl
      )

      queryClient.clear()
      successToast('Artist updated successfully!')
      setIsOpenModal(false)
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClickUpload = () => {
    document.getElementById('file')?.click()
  }

  return (
    <ModalWindow
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false)
      }}
    >
      <div className={classes.wrapper}>
        <div className={classes.headerWrapper}>
          <button
            onClick={() => {
              handleClickUpload()
            }}
            type="text"
            className={classes.imageContainer}
          >
            {selectedProfileImageURL || artist?.image?.url ? (
              <img
                className={classes.image}
                src={selectedProfileImageURL ?? artist?.image?.url}
              />
            ) : (
              <div className={classes.emptyImage}>
                <p className={classes.emptyImageText}>
                  {artist?.name.charAt(0).toUpperCase()}
                </p>
              </div>
            )}

            <ButtonIcon
              icon={<CameraIcon />}
              className={
                isSaving ? classes.photoButtonDisabled : classes.photoButton
              }
            />
            <input
              style={{ display: 'none' }}
              type="file"
              id="file"
              name="file"
              disabled={isSaving}
              accept="image/png, image/jpeg"
              onChange={e => {
                setSelectedProfileImageFile(e.target.files[0])
                setSelectecProfileImageURL(
                  URL.createObjectURL(e.target.files[0])
                )
              }}
            ></input>
          </button>
        </div>

        <div className={classes.fieldsWrapper}>
          {fieldsConfig
            .filter(({ key }) => {
              // Hide tour and ticketmaster fields for non-internal users
              if ((key === 'tour' || key === 'ticketmaster') && !isInternal) {
                return false;
              }
              return true;
            })
            .map(({ key, label, icon }) => (
            <div key={key}>
              <div className={classes.fieldTitleContainer}>
                {icon && <img className={classes.fieldTitleIcon} src={icon} />}
                <p className={classes.fieldTitle}>{label}</p>
              </div>
              <input
                className={classes.fieldInput}
                type="text"
                name={key}
                value={formData[key]}
                disabled={isSaving}
                autoComplete="off"
                placeholder={
                  label === 'Artist Name'
                    ? 'Name is required'
                    : label === 'Ticketmaster'
                    ? 'Enter Ticketmaster artist ID'
                    : 'Leave this empty to hide it from profile'
                }
                onChange={handleChange}
              />
              {errors[key] && <p className={classes.error}>{errors[key]}</p>}
            </div>
          ))}
        </div>
        <div className={classes.saveWrapper}>
          {isSaving ? (
            <ScaleLoader color="#FF00FF" loading={isSaving} />
          ) : (
            <ButtonOutline
              onClick={() => {
                handleOnSave()
              }}
              background="transparent"
              fontWeight={500}
              size={'small'}
              text={'SAVE'}
              color={'pinkBlack'}
            />
          )}
        </div>
      </div>
    </ModalWindow>
  )
}

ArtistEditData.propTypes = {
  isOpenModal: PropTypes.bool,
  setIsOpenModal: PropTypes.func,
  artist: PropTypes.object.isRequired,
}
